import React from 'react';
import { Trophy, Clock, Gamepad2, TrendingUp, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { UserGameStats } from '../types';

interface StatsOverviewProps {
  stats: UserGameStats;
  username: string;
}

export function StatsOverview({ stats, username }: StatsOverviewProps) {
  const formatPlaytime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    if (hours < 1) return 'Less than 1 hour';
    return `${hours.toLocaleString()} ${hours === 1 ? 'hour' : 'hours'}`;
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-100 rounded">
            <Clock className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <p className="text-sm text-gray-600">Total Playtime</p>
            <p className="text-xl font-semibold text-gray-900">
              {formatPlaytime(stats.totalPlaytime)}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded">
            <Gamepad2 className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <p className="text-sm text-gray-600">Games Owned</p>
            <p className="text-xl font-semibold text-gray-900">
              {stats.gameCount.toLocaleString()}
            </p>
          </div>
        </div>
      </div>

      <Link
        to={`/user/${username}/achievements`}
        className="group bg-white rounded-lg shadow p-4 hover:shadow-md transition-all hover:bg-indigo-50 cursor-pointer relative overflow-hidden"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded group-hover:bg-indigo-200 transition-colors">
            <Trophy className="w-6 h-6 text-indigo-600" />
          </div>
          <div className="flex-grow">
            <p className="text-sm text-gray-600 group-hover:text-indigo-600 transition-colors">Achievements Earned</p>
            <p className="text-xl font-semibold text-gray-900 group-hover:text-indigo-900 transition-colors">
              {stats.achievementStats.unlocked.toLocaleString()}
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-indigo-400 transform translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all" />
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-indigo-500 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left"></div>
      </Link>

      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-purple-100 rounded">
            <TrendingUp className="w-6 h-6 text-purple-600" />
          </div>
          <div>
            <p className="text-sm text-gray-600">Recent Activity</p>
            <p className="text-xl font-semibold text-gray-900">
              {formatPlaytime(stats.recentPlaytime)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}